import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, FileUpload, Textarea } from '@dreamtree-org/twreact-ui';
import { SectionCard, StatCard } from '@/components';
import { useMutation } from '@/lib/useQuery';
import { errorText } from '@/lib/format';
import { parseCsv } from '../lib/csv';
import { rowProblems } from '../lib/variantCsv';
import { initialMapping, isShopifyExport, mappingForServer, shopifyRecords } from '../lib/shopifyCsv';
import ShopifyMapping from './ShopifyMapping';

/**
 * Migrate from Shopify (MERCH-90): the merchant's product CSV export. "Check" is the server's dry run
 * (InventoryUnit shopifyPreview): every Shopify type with its counts and a first guess, which the merchant maps to one
 * of the store's types (or skips); one condition for the batch; a grade column when that condition needs one; the
 * required specs per type (Shopify has no column for them); Tags and metafield columns onto the store's own fields.
 * Every check re-runs the dry run with the mapping; "Import" runs it (importShopify): all or nothing, drafts only,
 * images from cdn.shopify.com only.
 */
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function Report({ report, form, setForm }) {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <StatCard label="Products in file" value={report.file.products} />
        <StatCard label="Will import" value={report.import.products + report.import.extended} />
        <StatCard label="Variants" value={report.import.variants} />
        <StatCard label="Images" value={report.images.importable} />
      </div>
      <ShopifyMapping report={report} form={form} setForm={setForm} />
      {report.images.other_hosts.length ? (
        <Alert variant="warning" title="Images on other sites are skipped">
          {report.images.other_hosts.map((h) => `${h.host} (${h.count})`).join(', ')}. Add them on the product page.
        </Alert>
      ) : null}
      {report.skipped.length ? <Alert variant="info" title={`${plural(report.skipped.length, 'product')} skipped`}>{report.skipped.map((s) => `${s.handle}: ${s.reason}`).join('; ')}</Alert> : null}
      {report.already_in_store.length ? <Alert variant="info" title={`${plural(report.already_in_store.length, 'line')} already in your store`}>These SKUs are already in your store, so their lines are skipped: {report.already_in_store.map((x) => `line ${x.row} (${x.sku})`).join('; ')}.</Alert> : null}
      {report.generated_skus.length ? <Alert variant="info" title={`${plural(report.generated_skus.length, 'SKU')} made for lines without one`}>{report.generated_skus.map((x) => `line ${x.row}: ${x.sku}`).join('; ')}</Alert> : null}
      {report.stock_zeroed.length ? <Alert variant="info" title="Stock set to 0">{report.stock_zeroed.map((z) => `line ${z.row}${z.sku ? ` (${z.sku})` : ''}: ${z.given == null ? 'blank' : z.given}`).join('; ')}</Alert> : null}
      {report.columns.not_imported.length ? <p className="text-xs opacity-70">Not imported: {report.columns.not_imported.join(', ')}.</p> : null}
    </div>
  );
}

function Problems({ report, failure }) {
  const issues = [...(report ? report.mapping_problems : []).map((p) => p.message), ...((failure && failure.mapping) || [])];
  const byRow = failure ? failure.byRow : rowProblems((report ? report.problems : []).map((p) => ({ path: `rows.${p.row}${p.path ? `.${p.path}` : ''}`, message: p.message })));
  if (!issues.length && !Object.keys(byRow).length && !failure) return null;
  return (
    <Alert variant="error" title={failure ? 'Nothing was imported' : 'Fix these before importing'}>
      {failure ? <p className="text-sm">{failure.message}</p> : null}
      <ul className="mt-2 list-disc pl-4 text-sm">
        {issues.map((m) => <li key={m}>{m}</li>)}
        {Object.entries(byRow).map(([row, msgs]) => <li key={row}>{Number(row) ? `Line ${row}` : 'File'}: {msgs.join('; ')}</li>)}
      </ul>
    </Alert>
  );
}

export default function ShopifyImport() {
  const nav = useNavigate();
  const check = useMutation('crud');
  const submit = useMutation('crud');
  const [files, setFiles] = useState([]);
  const [text, setText] = useState('');
  const [records, setRecords] = useState(null);
  const [fileError, setFileError] = useState('');
  const [report, setReport] = useState(null);
  const [form, setForm] = useState(null);
  const [failure, setFailure] = useState(null);
  const [done, setDone] = useState(null);
  // The mapping the last dry run checked: Import is offered only for it (any change needs a new check).
  const [checked, setChecked] = useState('');
  const current = form ? JSON.stringify(mappingForServer(form)) : '';

  const dryRun = async (rows, mapping) => {
    setFailure(null);
    try {
      const res = await check.run({ model: 'InventoryUnit', action: 'shopifyPreview', data: { rows, ...(mapping ? { mapping } : {}) } });
      const next = res.data || res;
      setReport(next);
      const used = mapping || mappingForServer(initialMapping(next));
      setChecked(JSON.stringify(used));
      if (!mapping) setForm(initialMapping(next));
    } catch (ex) {
      setFailure({ message: errorText(ex), byRow: rowProblems(ex && ex.response && ex.response.data && ex.response.data.details) });
    }
  };
  const read = (csv) => {
    setDone(null); setReport(null); setForm(null); setFailure(null);
    const table = parseCsv(csv);
    if (!isShopifyExport(table)) { setRecords(null); setFileError('This is not a Shopify product export (it needs Handle, Title and Variant Price columns). In Shopify: Products → Export → CSV for Excel, Numbers or other spreadsheet programs.'); return; }
    setFileError('');
    const rows = shopifyRecords(table);
    setRecords(rows);
    dryRun(rows, null);
  };
  const onFiles = async (list) => {
    setFiles(list);
    const f = list && list[0];
    if (!f) return;
    const content = await f.text();
    setText(content);
    read(content);
  };
  const run = async () => {
    setFailure(null);
    try {
      const res = await submit.run({ model: 'InventoryUnit', action: 'importShopify', data: { rows: records, mapping: mappingForServer(form) } });
      setDone(res.data || res);
    } catch (ex) {
      const data = (ex && ex.response && ex.response.data) || {};
      const details = Array.isArray(data.details) ? data.details : [];
      const mapping = details.filter((d) => String(d.path || '').startsWith('mapping.')).map((d) => d.message);
      setFailure({ message: errorText(ex), mapping, byRow: rowProblems(details.filter((d) => !String(d.path || '').startsWith('mapping.'))) });
    }
  };

  return (
    <SectionCard title="Migrate from Shopify">
      <p className="text-sm opacity-80">Export your products from Shopify (Products → Export, as CSV) and add the file. Nothing is saved until you import; everything imports as a draft for you to review and publish.</p>
      <FileUpload accept=".csv,text/csv" files={files} onFileSelect={onFiles} onFileRemove={() => { setFiles([]); setText(''); setRecords(null); setReport(null); }} maxSize={5 * 1024 * 1024} />
      <Textarea label="…or paste the CSV" rows={4} value={text} onChange={(e) => setText(e.target.value)} />
      <Button onClick={() => read(text)} loading={check.loading && !report} disabled={!text.trim()}>Check file</Button>
      {fileError ? <Alert variant="error">{fileError}</Alert> : null}
      {report && form ? (
        <div className="space-y-3" data-testid="shopify-import-review">
          <Report report={report} form={form} setForm={setForm} />
          <Problems report={report} failure={failure} />
          {done && done.status === 'nothing_new' ? (
            <Alert variant="info" title="Nothing new to import">Every line's SKU is already in your store.</Alert>
          ) : null}
          {done && done.status !== 'nothing_new' ? (
            <Alert variant="success" title="Import complete">
              Batch {done.batch_id}: {plural(done.variants, 'variant')}: {plural(done.products, 'new draft product')}{done.extended ? `, ${plural(done.extended, 'existing product')} given new variants` : ''}. Images: {done.images.stored} added{done.images.failed.length ? `, ${done.images.failed.length} could not be fetched` : ''}{done.images.not_fetched ? `, ${done.images.not_fetched} left to add by hand` : ''}.
              <div className="mt-2"><Button size="sm" variant="outline" onClick={() => nav('/products?tab=listed')}>View in Products</Button></div>
            </Alert>
          ) : null}
          {done ? null : (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => dryRun(records, mappingForServer(form))} loading={check.loading}>Check again with this mapping</Button>
              <Button onClick={run} loading={submit.loading} disabled={!report.ready || checked !== current}>Import {plural(report.import.variants, 'variant')}</Button>
              {report.ready && checked === current ? <Badge variant="success">Ready</Badge> : <span className="text-sm opacity-70">Check again after changing the mapping.</span>}
            </div>
          )}
        </div>
      ) : (failure ? <Problems report={null} failure={failure} /> : null)}
    </SectionCard>
  );
}
